import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import {
  CURRENCY_SYMBOLS,
  cleanNumber,
  isNumeric,
  getPercentStyle,
  formatInt,
  formatCurrency,
  type ColumnMeta,
  type ExportColumn,
} from "../Utils/anomaliesColumns";

interface Props {
  metadata: Record<string, ColumnMeta>;
  rows: any[];
}

// Rich render config for this tab's on-screen columns. Lives here (not in the
// shared utils) so the child owns its columns and the parent imports the
// derived CSV list below.
interface MissingColumn {
  key: string;
  label: string;
  showInfo?: boolean;
  /** custom info-tooltip; falls back to the metadata description */
  infoText?: string;
  /** count + coloured %CVR badge in the table; split into two CSV columns */
  percentKey?: string;
  /** CSV header for the split-out % column */
  percentLabel?: string;
  currency?: "USD";
  currencyKey?: string;
  isNumber?: boolean;
  isPercentPlain?: boolean;
  isDate?: boolean;
}

// Column order + labels follow the Figma (NOT output_metadata).
const MISSING_DATA_COLUMNS: MissingColumn[] = [
  { key: "brand_display_name", label: "Brand Name" },
  { key: "brand_id", label: "Brand ID" },
  { key: "brand_action_date", label: "Date", isDate: true },
  { key: "clicks", label: "Click Out", isNumber: true },
  {
    key: "ftd",
    label: "FTD (%CVR)",
    isNumber: true,
    percentKey: "FTD_perc",
    percentLabel: "FTD Perc",
    showInfo: true,
    infoText: "The percentage of FTD's of Click Outs",
  },
  {
    key: "qftd",
    label: "QFTD (%CVR)",
    isNumber: true,
    percentKey: "QFTD_perc",
    percentLabel: "QFTD Perc",
    showInfo: true,
    infoText: "The percentage of QFTD's of Click Outs",
  },
  { key: "cpa_income", label: "CPA Income", currencyKey: "cpa_currency_code" },
  {
    key: "revshare_income",
    label: "RS Income",
    currencyKey: "revshare_currency_code",
  },
  {
    key: "totalincome",
    label: "Total Income",
    currencyKey: "cpa_currency_code",
  },
  { key: "total_income_dollar", label: "Total Income $", currency: "USD" },
  { key: "net_rev", label: "NetRev", currency: "USD" },
  { key: "EPC", label: "EPC", currency: "USD" },
  { key: "ROI_perc", label: "ROI", isPercentPlain: true },
  { key: "PV", label: "PV", currency: "USD" },
];

// Plain-text value for a config cell — shared by the CSV export below.
const plainCell = (col: MissingColumn, row: any): string => {
  const value = row[col.key];
  if (value == null || value === "") return "";
  if (col.isDate) {
    const d = dayjs(value);
    return d.isValid() ? d.format("DD/MM/YYYY") : "";
  }
  if (col.currency) return formatCurrency(value, CURRENCY_SYMBOLS.USD);
  if (col.currencyKey) {
    const symbol = CURRENCY_SYMBOLS[row[col.currencyKey]] ?? "";
    return formatCurrency(value, symbol);
  }
  if (col.isPercentPlain) return `${Math.round(cleanNumber(value))}%`;
  if (col.isNumber) return formatInt(value);
  return String(value);
};

// CSV column list for the parent. Derived from the render config: %CVR columns
// split into a count column + a percent column, everything else plain. Static
// (no metadata needed), matching how every other Anomalies tab exports.
export const MISSING_DATA_EXPORT_COLUMNS: ExportColumn[] =
  MISSING_DATA_COLUMNS.flatMap((col) => {
    if (col.percentKey) {
      const percentKey = col.percentKey;
      return [
        {
          key: col.key,
          label: col.label,
          format: (v: any) => (v == null ? "" : formatInt(v)),
        },
        {
          key: percentKey,
          label: col.percentLabel ?? `${col.label} %`,
          format: (v: any) => (v == null ? "" : `${cleanNumber(v).toFixed(1)}%`),
        },
      ];
    }
    return [
      {
        key: col.key,
        label: col.label,
        format: (_v: any, row: any) => plainCell(col, row),
      },
    ];
  });

const AnomaliesMissingDataTable = ({ metadata, rows }: Props) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [sort, setSort] = React.useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "", direction: "desc" });

  // only keep configured columns whose key actually exists in the metadata
  const columns = React.useMemo(
    () => MISSING_DATA_COLUMNS.filter((c) => metadata && metadata[c.key]),
    [metadata],
  );

  // default sort key once columns exist
  React.useEffect(() => {
    if (columns.length > 0 && !sort.keytoSort) {
      setSort({ keytoSort: columns[0].key, direction: "desc" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  React.useEffect(() => {
    setPage(0);
  }, [rows]);

  const handleSort = (key: string) => {
    setSort((prev) => ({
      keytoSort: key,
      direction:
        prev.keytoSort === key && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(0);
  };

  const handleChangePage = (_: any, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const sortedRows = React.useMemo(() => {
    if (!sort.keytoSort) return rows;

    const sortCol = MISSING_DATA_COLUMNS.find((c) => c.key === sort.keytoSort);

    return [...rows].sort((a, b) => {
      const aRaw = a[sort.keytoSort];
      const bRaw = b[sort.keytoSort];
      if (aRaw == null && bRaw == null) return 0;
      if (aRaw == null) return 1;
      if (bRaw == null) return -1;

      if (sortCol?.isDate) {
        const diff = dayjs(aRaw).valueOf() - dayjs(bRaw).valueOf();
        return sort.direction === "asc" ? diff : -diff;
      }

      if (!isNumeric(aRaw) && !isNumeric(bRaw)) {
        return sort.direction === "asc"
          ? String(aRaw).localeCompare(String(bRaw))
          : String(bRaw).localeCompare(String(aRaw));
      }
      const aNum = cleanNumber(aRaw);
      const bNum = cleanNumber(bRaw);
      return sort.direction === "asc" ? aNum - bNum : bNum - aNum;
    });
  }, [rows, sort]);

  const renderCell = (col: MissingColumn, row: any) => {
    const value = row[col.key];

    // count + coloured %CVR badge (FTD / QFTD)
    if (col.percentKey) {
      const percent = cleanNumber(row[col.percentKey]);
      const s = getPercentStyle(percent);
      return (
        <Box display="flex" alignItems="center" gap={0.75}>
          <span>{value == null ? "" : formatInt(value)}</span>
          {row[col.percentKey] != null && (
            <Box
              component="span"
              sx={{
                backgroundColor: s.bg,
                color: s.color,
                border: `1px solid ${s.border}`,
                borderRadius: "4px",
                px: "6px",
                py: "1px",
                fontSize: "13px",
                fontWeight: 400,
              }}
            >
              {percent.toFixed(1)}%
            </Box>
          )}
        </Box>
      );
    }

    if (value == null || value === "") return "";

    if (col.isDate) {
      const d = dayjs(value);
      return d.isValid() ? d.format("DD/MM/YYYY") : "";
    }

    if (col.currency) return formatCurrency(value, CURRENCY_SYMBOLS.USD);
    if (col.currencyKey) {
      const symbol = CURRENCY_SYMBOLS[row[col.currencyKey]] ?? "";
      return formatCurrency(value, symbol);
    }
    if (col.isPercentPlain) return `${Math.round(cleanNumber(value))}%`;
    if (col.isNumber) return formatInt(value);
    return String(value);
  };

  return (
    <>
      <TableContainer>
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: "1800px", whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                // Prefer the custom %CVR message; fall back to metadata desc.
                const infoText =
                  col.infoText ?? metadata[col.key]?.description;
                return (
                  <TableCell
                    key={col.key}
                    align="left"
                    sortDirection={
                      sort.keytoSort === col.key ? sort.direction : false
                    }
                  >
                    <TableSortLabel
                      active={sort.keytoSort === col.key}
                      direction={
                        sort.keytoSort === col.key ? sort.direction : "desc"
                      }
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                      {col.showInfo && infoText && (
                        <Tooltip
                          title={infoText}
                          arrow
                          placement="top"
                          slotProps={{
                            tooltip: {
                              sx: {
                                maxWidth: 200,
                                whiteSpace: "normal",
                                textAlign: "center",
                              },
                            },
                          }}
                        >
                          <InfoOutlinedIcon
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              fontSize: 16,
                              color: "gray",
                              cursor: "pointer",
                              ml: 0.5,
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows?.length > 0 &&
              sortedRows
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => (
                  <TableRow
                    key={row.brand_id ?? row.brand_display_name ?? rowIndex}
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.key} align="left">
                        {renderCell(col, row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            {sortedRows?.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ py: 3 }}
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {sortedRows?.length > 0 && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginLeft: "15px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(rows?.length / rowsPerPage)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
          />
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={rows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingRight: "16px",
              },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default AnomaliesMissingDataTable;